import { useEffect, useState } from 'react';
import { useAuthStore, useSubscriptionStore } from '../lib/store';
import { checkSubscription, listClips, type Clip } from '../lib/api';
import { LayoutDashboard, FileText, CheckCircle, TrendingUp, LogOut, Video, CreditCard, Settings } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

export default function DashboardPage() {
  const { user, logout, subscription: authSubscription } = useAuthStore();
  const { subscription, setSubscription } = useSubscriptionStore();
  const [clips, setClips] = useState<Clip[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  
  const hasActiveSubscription = authSubscription?.active && authSubscription?.expiry && authSubscription.expiry > Date.now();

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    try {
      const [subData, clipsData] = await Promise.all([
        checkSubscription(user!.id),
        listClips(user!.id),
      ]);
      setSubscription(subData);
      setClips(clipsData.clips);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const pendingApproval = clips.filter((c) => c.pa_approved === 0).length;
  const approvedClips = clips.filter((c) => c.pa_approved === 1).length;
  const avgVirality = clips.length > 0
    ? Math.round(clips.reduce((sum, c) => sum + c.virality_score, 0) / clips.length)
    : 0;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              CLIPD
            </h1>
            <p className="text-sm text-slate-500">Dashboard ya Mteja</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm font-medium text-slate-700">{user?.email}</p>
              <p className={`text-xs ${subscription?.active ? 'text-green-600' : 'text-red-600'}`}>
                {subscription?.active ? 'Active' : 'Expired'}
              </p>
            </div>
            <button
              onClick={logout}
              className="p-2 text-slate-500 hover:text-slate-700 transition-colors"
            >
              <LogOut size={20} />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Subscription Banner */}
        {!hasActiveSubscription && user?.role !== 'admin' && (
          <div className="bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-xl p-6 mb-6">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold text-orange-900 mb-2">
                  Subscription Imekisha
                </h3>
                <p className="text-sm text-orange-700 mb-4">
                  Lipia TSh 5,000 kufungua huduma zote za CLIPD kwa masaa 24.
                </p>
                <button
                  onClick={() => navigate('/payment')}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center gap-2"
                >
                  <CreditCard size={18} />
                  Lipia Sasa
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Link
            to="/process"
            className={`bg-white rounded-xl p-6 shadow-sm border border-slate-200 hover:shadow-md transition-shadow ${
              !hasActiveSubscription && user?.role !== 'admin' ? 'opacity-50 pointer-events-none' : ''
            }`}
          >
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-50 rounded-lg">
                <Video size={24} className="text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">Process Video</h3>
                <p className="text-sm text-slate-500">Badilisha hotuba kuwa clips</p>
              </div>
            </div>
          </Link>

          <Link
            to="/clips"
            className="bg-white rounded-xl p-6 shadow-sm border border-slate-200 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-50 rounded-lg">
                <FileText size={24} className="text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-800">View Clips</h3>
                <p className="text-sm text-slate-500">Angalia klipu zako zote</p>
              </div>
            </div>
          </Link>

          {user?.role === 'admin' && (
            <Link
              to="/admin"
              className="bg-white rounded-xl p-6 shadow-sm border border-slate-200 hover:shadow-md transition-shadow"
            >
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-50 rounded-lg">
                  <Settings size={24} className="text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800">Admin Dashboard</h3>
                  <p className="text-sm text-slate-500">Simamia watumiaji na malipo</p>
                </div>
              </div>
            </Link>
          )}
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <StatCard
            icon={<FileText size={24} />}
            title="Jumla ya Klipu"
            value={clips.length.toString()}
            color="blue"
          />
          <StatCard
            icon={<CheckCircle size={24} />}
            title="Zimeidhinishwa"
            value={approvedClips.toString()}
            color="green"
          />
          <StatCard
            icon={<LayoutDashboard size={24} />}
            title="Zinasubiri Approval"
            value={pendingApproval.toString()}
            color="orange"
          />
          <StatCard
            icon={<TrendingUp size={24} />}
            title="Virality Score"
            value={avgVirality.toString()}
            color="purple"
          />
        </div>

        {/* Recent Clips */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200">
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="text-lg font-semibold text-slate-800">Klipu za Hivi Karibuni</h2>
          </div>
          <div className="divide-y divide-slate-200">
            {loading ? (
              <div className="p-6 text-center text-slate-500">Inapakia...</div>
            ) : clips.length === 0 ? (
              <div className="p-6 text-center text-slate-500">Hakuna klipu bado</div>
            ) : (
              clips.slice(0, 5).map((clip) => (
                <Link
                  key={clip.id}
                  to={`/approval/${clip.id}`}
                  className="block px-6 py-4 hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="font-medium text-slate-800">{clip.title}</h3>
                      <p className="text-sm text-slate-500 mt-1 line-clamp-2">
                        {clip.transcript.substring(0, 100)}...
                      </p>
                    </div>
                    <div className="ml-4 flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-sm font-medium text-slate-700">
                          Score: {clip.virality_score}
                        </p>
                        <p className="text-xs text-slate-500">
                          {clip.duration.toFixed(1)}s
                        </p>
                      </div>
                      {clip.pa_approved === 1 ? (
                        <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                          Approved
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full">
                          Pending
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

interface StatCardProps {
  icon: React.ReactNode;
  title: string;
  value: string;
  color: 'blue' | 'green' | 'orange' | 'purple';
}

function StatCard({ icon, title, value, color }: StatCardProps) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    orange: 'bg-orange-50 text-orange-600',
    purple: 'bg-purple-50 text-purple-600',
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
      <div className="flex items-center gap-4">
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>{icon}</div>
        <div>
          <p className="text-sm text-slate-500">{title}</p>
          <p className="text-2xl font-bold text-slate-800">{value}</p>
        </div>
      </div>
    </div>
  );
}
